import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi, type Sim } from "../../lib/admin";
import { Drawer, Field, Notice, Pill, Toolbar, clean, fmtDate } from "./ui";

type Tab = "blank" | "requested" | "stock" | "active" | "all";
const TABS: [Tab, string][] = [["blank", "Blank stock"], ["requested", "Awaiting number"], ["stock", "Available"], ["active", "In devices"], ["all", "All"]];
const STATUS_LABEL: Record<string, string> = {
  blank: "Blank", requested: "Awaiting number", stock: "Available", active: "In device", suspended: "Suspended", retired: "Retired",
};

export default function Sims() {
  const [sims, setSims] = useState<Sim[] | null>(null);
  const [inDevice, setInDevice] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<Tab>("stock");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<Sim | "new" | null>(null);
  const [bulk, setBulk] = useState(false);
  const [activate, setActivate] = useState<Sim | null>(null);
  const [msg, setMsg] = useState<{ kind?: "ok" | "bad"; text: string } | null>(null);

  const load = useCallback(async () => {
    const [{ data: s }, { data: d }] = await Promise.all([
      supabase.from("sims").select("*").order("received_at", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false }),
      supabase.rpc("admin_devices"),
    ]);
    setSims((s ?? []) as Sim[]);
    setInDevice(Object.fromEntries(((d ?? []) as any[]).filter((x) => x.sim_id).map((x) => [x.sim_id, x.name])));
  }, []);
  useEffect(() => { load(); }, [load]);

  const count = (t: Tab) => (sims ?? []).filter((s) => t === "all" || s.status === t).length;
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (sims ?? []).filter((s) => (tab === "all" || s.status === tab) &&
      (!t || [s.msisdn, s.iccid, s.carrier, s.plan, s.batch, inDevice[s.id]].some((v) => v?.toLowerCase().includes(t))));
  }, [sims, q, tab, inDevice]);
  const nextBlank = (sims ?? []).find((s) => s.status === "blank"); // newest received first

  async function step(s: Sim, what: "request" | "cancel-request") {
    try {
      await adminApi("POST", `sims/${s.id}/${what}`, {});
      setMsg({ text: what === "request" ? `Marked …${s.iccid?.slice(-6)} as requested — enter the number when Vodafone confirms.` : "Request cancelled." });
      await load();
    } catch (e: any) { setMsg({ kind: "bad", text: e.message }); }
  }
  const copy = (t: string) => navigator.clipboard?.writeText(t).catch(() => {});

  return (
    <section>
      <Toolbar>
        <h1>SIM cards</h1>
        <input className="asearch" placeholder="Search number, ICCID, batch, device…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="rep-actions">
          <button className="btn-ghost" onClick={() => setBulk(true)}>+ Bulk add blank SIMs</button>
          <button className="btn-primary" onClick={() => setEdit("new")}>+ Add SIM</button>
        </div>
      </Toolbar>
      <nav className="subtabs">
        {TABS.map(([t, l]) => <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>{l} <span className="count">{count(t)}</span></button>)}
      </nav>
      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}

      {tab === "blank" && nextBlank && (
        <div className="await next-sim">
          <div>
            <h3>Next SIM to use</h3>
            <p className="sub">Newest blank SIM received. Give this ICCID to Vodafone, then mark it requested.</p>
            <div className="iccid mono">{fmtIccid(nextBlank.iccid)}</div>
            <div className="sub">{nextBlank.carrier} · received {fmtDate(nextBlank.received_at)}{nextBlank.batch ? ` · batch ${nextBlank.batch}` : ""}</div>
          </div>
          <div className="next-actions">
            <button className="btn-ghost" onClick={() => copy(nextBlank.iccid ?? "")}>Copy ICCID</button>
            <button className="btn-ghost" onClick={() => step(nextBlank, "request")}>Mark requested</button>
            <button className="btn-primary" onClick={() => setActivate(nextBlank)}>Enter number</button>
          </div>
        </div>
      )}

      <div className="atable-wrap">
        <table className="atable static">
          <thead><tr><th>Number</th><th>ICCID</th><th>Carrier / plan</th><th>Batch · received</th><th>In device</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {sims === null && <tr className="empty"><td colSpan={7}>Loading…</td></tr>}
            {rows.map((s) => (
              <tr key={s.id}>
                <td className="mono"><b>{s.msisdn ?? "—"}</b></td>
                <td className="mono">{s.iccid ? <button className="linkish mono" title="Copy" onClick={() => copy(s.iccid!)}>{fmtIccid(s.iccid)}</button> : "—"}</td>
                <td>{s.carrier ?? "—"}<div className="sub">{s.plan ?? ""}</div></td>
                <td>{s.batch ?? "—"}<div className="sub">{fmtDate(s.received_at)}</div></td>
                <td>{inDevice[s.id] ?? <span className="sub">—</span>}</td>
                <td><Pill value={s.status === "requested" ? "invited" : s.status} label={STATUS_LABEL[s.status]} />
                  {s.status === "requested" && <div className="sub">since {fmtDate(s.requested_at)}</div>}</td>
                <td className="row-actions">
                  {s.status === "blank" && <button className="btn-ghost small" onClick={() => step(s, "request")}>Requested</button>}
                  {(s.status === "blank" || s.status === "requested") && <button className="btn-ghost small" onClick={() => setActivate(s)}>Enter number</button>}
                  {s.status === "requested" && <button className="btn-ghost small" onClick={() => step(s, "cancel-request")}>Cancel</button>}
                  <button className="btn-ghost small" onClick={() => setEdit(s)}>Edit</button>
                </td>
              </tr>
            ))}
            {sims && !rows.length && <tr className="empty"><td colSpan={7}>No SIM cards here.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <SimForm sim={edit === "new" ? null : edit} device={edit !== "new" ? inDevice[edit.id] : undefined}
        onClose={() => setEdit(null)} onSaved={async (m) => { setEdit(null); setMsg({ text: m }); await load(); }} />}
      {bulk && <BulkForm onClose={() => setBulk(false)} onSaved={async (m) => { setBulk(false); setMsg({ text: m }); setTab("blank"); await load(); }} />}
      {activate && <ActivateForm sim={activate} onClose={() => setActivate(null)}
        onSaved={async (m) => { setActivate(null); setMsg({ text: m }); setTab("stock"); await load(); }} />}
    </section>
  );
}

const fmtIccid = (s: string | null) => (s ? s.replace(/(.{4})/g, "$1 ").trim() : "—");

function BulkForm({ onClose, onSaved }: { onClose: () => void; onSaved: (m: string) => void }) {
  const [text, setText] = useState("");
  const [carrier, setCarrier] = useState("Vodafone");
  const [batch, setBatch] = useState("");
  const [received, setReceived] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const tokens = text.split(/[\s,;]+/).map((t) => t.replace(/[^0-9A-Fa-f]/g, "")).filter(Boolean);
  const bad = tokens.filter((t) => !/^\d{18,22}F?$/i.test(t));
  const unique = new Set(tokens).size;

  async function readFile(f: File) {
    const t = await f.text();
    // accept CSV / text: pull every 18–22 digit run
    setText((t.match(/\d{18,22}/g) ?? []).join("\n"));
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await adminApi<{ added: number; duplicates_in_db: string[]; duplicates_in_list: number }>("POST", "sims/bulk",
        clean({ iccids: text, carrier, batch, received_at: received }));
      const skipped = r.duplicates_in_db.length + r.duplicates_in_list;
      onSaved(`Added ${r.added} blank SIM${r.added === 1 ? "" : "s"}${skipped ? ` · ${skipped} duplicate${skipped === 1 ? "" : "s"} skipped` : ""}.`);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Drawer title="Bulk add blank SIMs" onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="bulk-form" disabled={busy || !tokens.length || bad.length > 0}>{busy ? "Adding…" : bad.length ? "Fix invalid numbers first" : `Add ${unique || ""} SIM${unique === 1 ? "" : "s"}`}</button></>}>
      <form id="bulk-form" className="aform" onSubmit={save}>
        <Field label="ICCIDs" wide hint="Paste or scan one per line (spaces, commas also fine). The ICCID is the long number printed on the SIM card or its carrier card.">
          <textarea className="mono" rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder={"8967 9010 2345 6789 012\n8967 9010 2345 6789 013\n…"} />
        </Field>
        <Field label="Or import a file" wide hint="CSV or text — every 18–22 digit number is picked up.">
          <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => e.target.files?.[0] && readFile(e.target.files[0])} />
        </Field>
        <Field label="Carrier"><select value={carrier} onChange={(e) => setCarrier(e.target.value)}><option>Vodafone</option><option>Digicel</option><option>Other</option></select></Field>
        <Field label="Received"><input type="date" value={received} onChange={(e) => setReceived(e.target.value)} /></Field>
        <Field label="Batch / reference" wide><input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="e.g. VF-2026-10 delivery note 4471" maxLength={60} /></Field>
        <div className="wide kv">
          <span>Found</span><b>{tokens.length} number{tokens.length === 1 ? "" : "s"} ({unique} unique)</b>
          <span>Invalid</span><span className={bad.length ? "warn-text" : ""}>{bad.length ? bad.slice(0, 3).join(", ") + (bad.length > 3 ? "…" : "") : "none"}</span>
        </div>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}

function ActivateForm({ sim, onClose, onSaved }: { sim: Sim; onClose: () => void; onSaved: (m: string) => void }) {
  const [msisdn, setMsisdn] = useState("");
  const [plan, setPlan] = useState(sim.plan ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await adminApi("POST", `sims/${sim.id}/activate`, { msisdn, plan: plan.trim() || null });
      onSaved(`SIM ${msisdn} is now available to put in a device.`);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Drawer title="Enter SIM number" onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="act-form" disabled={busy}>{busy ? "Saving…" : "Make available"}</button></>}>
      <form id="act-form" className="aform" onSubmit={save}>
        <div className="wide kv"><span>ICCID</span><b className="mono">{fmtIccid(sim.iccid)}</b><span>Carrier</span><span>{sim.carrier}</span></div>
        <Field label="Phone number from the carrier" hint="e.g. 2223727"><input className="mono" value={msisdn} onChange={(e) => setMsisdn(e.target.value)} required maxLength={20} autoFocus /></Field>
        <Field label="Plan"><input value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="e.g. Data 1 GB/month" maxLength={60} /></Field>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}

function SimForm({ sim, device, onClose, onSaved }: { sim: Sim | null; device?: string; onClose: () => void; onSaved: (m: string) => void }) {
  const [f, setF] = useState({ msisdn: sim?.msisdn ?? "", iccid: sim?.iccid ?? "", carrier: sim?.carrier ?? "Vodafone",
    plan: sim?.plan ?? "", status: sim?.status ?? "stock", notes: sim?.notes ?? "", batch: sim?.batch ?? "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const noNumber = f.status === "blank" || f.status === "requested";

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const { batch: _b, ...body } = f;
      if (sim) await adminApi("PATCH", `sims/${sim.id}`, clean(body));
      else await adminApi("POST", "sims", clean(body));
      onSaved(`Saved SIM ${f.msisdn || f.iccid}.`);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <Drawer title={sim ? `SIM ${sim.msisdn ?? fmtIccid(sim.iccid)}` : "Add SIM card"} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="sim-form" disabled={busy}>{busy ? "Saving…" : "Save"}</button></>}>
      <form id="sim-form" className="aform" onSubmit={save}>
        <Field label="Phone number" hint={noNumber ? "Use “Enter number” in the list once the carrier confirms." : "Fiji numbers are 7 digits, e.g. 2223727"}>
          <input className="mono" value={f.msisdn} onChange={set("msisdn")} required={!sim} disabled={!!sim && noNumber} maxLength={20} />
        </Field>
        <Field label="Carrier">
          <select value={f.carrier} onChange={set("carrier")}><option>Vodafone</option><option>Digicel</option><option>Other</option></select>
        </Field>
        <Field label="ICCID" hint="The long number printed on the SIM card"><input className="mono" value={f.iccid} onChange={set("iccid")} maxLength={22} /></Field>
        <Field label="Plan"><input value={f.plan} onChange={set("plan")} placeholder="e.g. Data 1 GB/month" maxLength={60} /></Field>
        <Field label="Status" hint={device ? `Currently in ${device}. Assign SIMs from the device form.` : "SIMs become In device automatically when assigned."}>
          <select value={f.status} onChange={set("status")} disabled={noNumber}>
            {noNumber && <option value={f.status}>{STATUS_LABEL[f.status]}</option>}
            <option value="stock">Available</option><option value="active">In device</option>
            <option value="suspended">Suspended</option><option value="retired">Retired</option>
          </select>
        </Field>
        <Field label="Notes" wide><textarea value={f.notes} onChange={set("notes")} rows={2} maxLength={2000} /></Field>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}
